import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useAppSelector } from '../../reduxService/hooks';
import { selectDynamicProject } from '../Dynamic/store/project/projectslice';
import { RootState } from '../../reduxService';
import { EProjectType } from '../enum/project';
import { IDynamicProjectOutputData } from '../api/IApiResponse';

/**
 * Custom hook to centralize access to project-related data
 * Provides a single source of truth for project state across components
 *
 * @returns Object containing all project-related data and computed values
 */
export const useProjectData = () => {
    // Core project data from Redux
    const dynamicProject = useAppSelector(selectDynamicProject);
    const dynamicProjectData = useAppSelector((state: RootState) => state.Dynamic.project?.data);
    const resultsData = useSelector((state: RootState) => state.Dynamic.results.data?.outputData) as IDynamicProjectOutputData | undefined;

    // Form submission state
    const isFormSubmitting = useAppSelector(state => state.formControl.isSubmitting);

    // Derived values with memoization for performance
    const projectType = useMemo(() => dynamicProject.data?.type || EProjectType.container_yard, [dynamicProject.data?.type]);

    const projectGuid = useMemo(() => dynamicProjectData?.guid, [dynamicProjectData?.guid]);

    const hasResults = useMemo(() => !!resultsData, [resultsData]);

    const isNewProject = useMemo(() => dynamicProject.data?.newProject, [dynamicProject.data?.newProject]);

    return {
        // Raw data
        dynamicProject,
        dynamicProjectData,
        resultsData,

        // Derived computed values
        projectType,
        projectGuid,
        hasResults,
        isNewProject,

        // Form state
        isFormSubmitting,
    };
};
